import { action, observable } from 'mobx';
import { ProjectsStore } from './ProjectsStore';
import {
  FeatureLayer,
  SimpleMarkerSymbol,
  Point,
  Graphic,
  SpatialReference,
  SimpleRenderer,
  MapView,
  FeatureSet,
} from 'cd-eft/utils/esriTypes';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import Polyline from '@arcgis/core/geometry/Polyline';
import { drawRectangularBase, insertNewLineInBetweenTwoLines, putStructureRepOnTop } from '../../utils/gisUtils';
import { LinearUnit, constructPadLayer } from '../../utils/structureLayerUtils';
import Layer from '@arcgis/core/layers/Layer';
import { appConfig } from 'cd-eft/config/AppConfig';
import { map } from 'd3';
import { commonFields } from 'cd-eft/utils/gisUtils';
import { INotification } from 'cd-eft/models/Stores/DesignStore';

class PadDesignProjectsStore extends ProjectsStore {
  constructor(private paddesignstore: any) {
    super(paddesignstore);
  }
}

export class PadDesignStore {
  constructor(private appStore: any) {
    this.projectsStore = new PadDesignProjectsStore(this);
    this.init();
  }

  private _currentWkid = appConfig.tempHardCodes.wkid;

  @observable projectsStore: PadDesignProjectsStore;
  @observable hasGrid: boolean = false;
  @observable hasDesign: boolean = false;
  @observable editDesign: boolean = false;
  @observable newPad!: INewPad;
  @observable newDesignForm!: INewPadForm;
  @observable chooseCenter: boolean = false;
  @observable newPadResults?: INewPadResults = undefined;
  @observable padSummaryTable: any = [];
  @observable projectMetadata;
  @observable selectedPadsInfo: ISelectedPadsInfo[] = [];
  @observable displayedPadsIds: number[] = [];
  @observable latestElementId?: number;
  @observable showLoader: boolean = false;
  @observable surveyListDataGridTabs = ['Pad Summary', 'New Pad'];
  @observable currentTab: string = 'Pad Summary';
  @observable newDesign: boolean = false;
  @observable currentDesignLayer: GroupLayer = new GroupLayer();
  @observable originalDesignLayer: GroupLayer | undefined;
  @observable originalPadResults: any = {};
  @observable originalIdString: string = '';
  @observable padLayers: Layer[] = [];
  @observable pendingEdits: IPendingEdits = { name: false, design: false, unsavedEdits: true };
  @observable notification: INotification = {
    message: '',
    header: '',
    type: '',
    hidden: true,
    timestamp: undefined,
  };

  @action.bound showNotification(msg: string, header: string, type: string = 'error') {
    this.notification = {
      message: msg,
      header: header,
      type: type,
      hidden: false,
      timestamp: new Date(),
    };
  }

  @action.bound clearNotification() {
    this.notification = { message: '', header: '', type: '', hidden: true, timestamp: undefined };
  }

  @action changeTab(tab) {
    this.currentTab = tab;
  }

  @action reset() {
    this.init();
  }

  @action init() {
    this.resetNewPadForm();
    this.clearNotification();
    this.padSummaryTable = [];
    this.selectedPadsInfo = [];
    this.displayedPadsIds = [];
    this.chooseCenter = false;
    this.hasDesign = false;
    this.editDesign = false;
    this.showLoader = false;
    this.newDesign = false;
    this.currentTab = 'Pad Summary';
    this.newPad = {
      padName: '',
    };

    this.projectMetadata = {
      projectName: '',
      surfaceName: '',
      surfaceType: '',
    };
    this.hasGrid = false;
    this.padLayers = [];
  }

  @action resetNewPadForm() {
    this.newDesignForm = {
      name: '',
      type: 'Pad',
      equalCutSlopes: true,
      equalFillSlopes: true,
      center: {
        pointName: 'center',
        x: 0,
        y: 0,
        z: 0,
      },
      width: 10,
      length: 10,
      orienAzimuth: 0,
      designElementId: 0,
      cutSlopes: [4, 4, 4, 4],
      fillSlopes: [4, 4, 4, 4],
    };
    this.pendingEdits = {
      name: false,
      design: false,
      unsavedEdits: true
    };
  }

  @action cancelDesignElement() {
    this.changeTab('Pad Summary');
    this.newPadResults = undefined;
    this.hasDesign = false;
    this.editDesign = false;
    this.newDesign = false;
    this.originalDesignLayer = undefined;
    this.originalIdString = '';
    this.originalPadResults = undefined;
    this.resetNewPadForm();
  }

  // GIS FUNCTIONS

  private createPadPointsFeatureSet(padPoints: any[], projectSR: SpatialReference): FeatureSet {
    const spatialReference = projectSR;

    const graphics = padPoints.map((r, i) => {
      return new Graphic({
        geometry: new Point({
          spatialReference: spatialReference,
          hasZ: true,
          x: r.x,
          y: r.y,
          z: r.z,
        }),
        attributes: {
          ObjectID: i,
          pointName: r.pointName,
          y: r.y,
          x: r.x,
          z: r.z,
          description: r.description,
          // id: r.id,
          // visible: r.visible,
          // tinable: r.tinable,
          // editing_status: r.editing_status,
        },
      });
    });
    const fields = commonFields;

    const featureSet = new FeatureSet({
      features: graphics,
      fields: fields,
      geometryType: 'point',
      spatialReference: spatialReference,
      displayFieldName: 'pointName',
    });

    return featureSet;
  }

  private createPadRaysFeatureSet(rays: any[], projectSR): FeatureSet {
    const spatialReference = projectSR;

    const graphics = !rays
      ? []
      : rays.map((ray, i) => {
        const point1 = new Point({
          spatialReference: spatialReference,
          hasZ: true,
          x: ray[0].x,
          y: ray[0].y,
          z: ray[0].z,
        });

        const point2 = new Point({
          spatialReference: spatialReference,
          hasZ: true,
          x: ray[1].x,
          y: ray[1].y,
          z: ray[1].z,
        });

        const polyline = new Polyline({
          spatialReference: spatialReference,
          hasZ: true,
        });
        polyline.addPath([point1, point2]);

        const graphic = new Graphic({
          geometry: polyline,
          attributes: {
            ObjectID: i,
          },
        });
        return graphic;
      });

    const fields: any = [
      {
        name: 'ObjectID',
        alias: 'ObjectID',
        type: 'oid',
      },
    ];

    const featureSet = new FeatureSet({
      features: graphics,
      fields: fields,
      geometryType: 'polyline',
      spatialReference: spatialReference,
      displayFieldName: 'ObjectID',
    });

    return featureSet;
  }

  private createPadBorderLayer(pad: any, projectSR: SpatialReference): FeatureLayer {
    const featureSet = this.createPadPointsFeatureSet(pad.borders, projectSR);

    const source = featureSet.features;

    // create feature layer
    const markerSymbol = new SimpleMarkerSymbol({
      color: [255, 0, 0],
      size: 6,
      outline: {
        color: [0, 0, 0],
        width: 0.5,
      },
    });

    const pointlayer = new FeatureLayer({
      source,
      spatialReference: featureSet.spatialReference,
      renderer: new SimpleRenderer({ symbol: markerSymbol }),
      fields: featureSet.fields,
      objectIdField: featureSet.fields.filter((field) => {
        return field.type === 'oid';
      })[0].name,
    });

    return pointlayer;
  }

  private createPadVertexLayer(pad: any, projectSR: SpatialReference): FeatureLayer {
    const featureSet = this.createPadPointsFeatureSet(pad.vertices, projectSR);

    const source = featureSet.features;

    // create feature layer
    const markerSymbol = new SimpleMarkerSymbol({
      color: [255, 0, 0],
      size: 6,
      outline: {
        color: [0, 0, 0],
        width: 0.5,
      },
    });

    const pointlayer = new FeatureLayer({
      source,
      spatialReference: featureSet.spatialReference,
      renderer: new SimpleRenderer({ symbol: markerSymbol }),
      fields: featureSet.fields,
      objectIdField: featureSet.fields.filter((field) => {
        return field.type === 'oid';
      })[0].name,
    });

    return pointlayer;
  }

  private createPadBreaklinesLayer(pad: any, projectSR: SpatialReference): FeatureLayer {
    const featureSet = this.createPadRaysFeatureSet(pad.breaklines, projectSR);
    const source = featureSet.features;

    // create feature layer
    const symbol = new SimpleLineSymbol({
      color: 'black',
      width: 0.5,
    });

    const layer = new FeatureLayer({
      source,
      spatialReference: featureSet.spatialReference,
      renderer: new SimpleRenderer({ symbol: symbol }),
      fields: featureSet.fields,
      objectIdField: featureSet.fields.filter((field) => {
        return field.type === 'oid';
      })[0].name,
    });

    return layer;
  }

  private createPadRaysLayer(pad: any, projectSR: SpatialReference): FeatureLayer {
    const featureSet = this.createPadRaysFeatureSet(pad.rays, projectSR);
    const source = featureSet.features;

    // create feature layer
    const symbol = new SimpleLineSymbol({
      color: 'black',
      width: 0.5,
    });

    const layer = new FeatureLayer({
      source,
      spatialReference: featureSet.spatialReference,
      renderer: new SimpleRenderer({ symbol: symbol }),
      fields: featureSet.fields,
      objectIdField: featureSet.fields.filter((field) => {
        return field.type === 'oid';
      })[0].name,
    });

    return layer;
  }

  private createLayerPadSlopeDirections(pad: any, projectSR: SpatialReference): FeatureLayer {
    const spatialReference = projectSR;

    const rays = pad.rays;
    const vertices = pad.vertices;

    // create slope direction graphics
    // note: rays are in random order! have to use vertices to get the correct order
    const graphics: __esri.Graphic[] = [];

    if (pad.rays) {
      for (let index = 0; index < vertices.length; index++) {
        const v1 = vertices[index];
        const v2 = index + 1 < vertices.length ? vertices[index + 1] : vertices[0];

        const ray1 = rays.find((r) => r[0].x === v1.x && r[0].y === v1.y);
        const ray2 = rays.find((r) => r[0].x === v2.x && r[0].y === v2.y);

        const slopePath = insertNewLineInBetweenTwoLines(ray1, ray2);

        const flowGraphic = new Graphic({
          geometry: new Polyline({
            spatialReference,
            hasZ: true,
            paths: [slopePath],
          }),
          attributes: {
            ObjectID: index,
          },
        });
        graphics.push(flowGraphic);
      }
    }

    // arrow symbol
    const arrowSymbol = new SimpleLineSymbol({
      color: 'blue',
      width: 3,
      marker: {
        color: 'blue',
        placement: 'begin',
        style: 'arrow',
      },
    });

    // create feature layer
    const layer = new FeatureLayer({
      source: graphics,
      spatialReference: spatialReference,
      renderer: new SimpleRenderer({ symbol: arrowSymbol }),
      fields: [
        {
          name: 'ObjectID',
          alias: 'ObjectID',
          type: 'oid',
        },
      ],
      objectIdField: 'ObjectID',
    });

    return layer;
  }

  public createPadLayer(pad: any, mapView: MapView, projectSR: SpatialReference, id, label?: any): GroupLayer {
    const wkid: number = appConfig.tempHardCodes.wkid;
    const inputSR: SpatialReference = projectSR;
    const outputSR: SpatialReference = mapView.spatialReference;
    const inputUnit: LinearUnit = 'intl-feet';
    const outputUnit: LinearUnit = 'meters';
    const padLayer = constructPadLayer(pad, inputSR, outputSR, inputUnit, outputUnit, id, label);
    return padLayer;
  }

  @action.bound removeAllPadLayers(view: MapView) {
    while (this.padLayers.length > 0) {
      const landLevelingLayer = this.padLayers.shift();
      if (landLevelingLayer) {
        view!.map.remove(landLevelingLayer);
      }
    }
    this.padLayers = [];
  }

  @action addPadToMap(pad: any, mapView: MapView, isCurrentDesignLayer: boolean, projectSR: SpatialReference, id?: string, label?: any): Layer {
    const padLayer = this.createPadLayer(pad, mapView, projectSR, id, label);
    if (id) {
      padLayer.id = id;
    }
    if (isCurrentDesignLayer && this.currentDesignLayer) {
      mapView.map.remove(this.currentDesignLayer);
    }
    mapView.map.add(padLayer);
    if (isCurrentDesignLayer) {
      this.currentDesignLayer = padLayer;
      if (id) {
        const padEntry = (this.padSummaryTable as any[]).find((entry) => entry.designElementId === id);
        const layerIndex = this.padLayers.findIndex((layer) => layer.id === id.toString());
        if (layerIndex !== -1) {
          this.padLayers.splice(layerIndex, 1);
        }
        if (padEntry && padEntry.checked) this.padLayers.push(padLayer);
      }
    } else {
      this.padLayers.push(padLayer);
    }
    // Ensure that the currentDesignLayer is updated correctly
    this.currentDesignLayer = padLayer;
    drawRectangularBase(mapView, this.newDesignForm, projectSR);
    putStructureRepOnTop(mapView, pad.name);
    return padLayer;
  }
}
// PadStore Interfaces
interface INewPad {
  padName: string;
}

export interface INewPadForm {
  name: string;
  type: string;
  equalCutSlopes: boolean;
  equalFillSlopes: boolean;
  center: ICenterObject;
  width: number;
  length: number;
  orienAzimuth: number;
  designElementId: number;
  cutSlopes: number[];
  fillSlopes: number[];
}

interface ICenterObject {
  pointName: string;
  x: number;
  y: number;
  z: number;
}

interface responseDataObject {
  description: any;
  id: number;
  keyString: any;
  objectName: any;
  pointName: null | string;
  tinnable: boolean;
  visible: boolean;
  x: number;
  y: number;
  z: number;
}

interface INewPadResults {
  borders: responseDataObject;
  center: responseDataObject;
  cutFillBalance: number;
  cutFillRatio: number;
  cutSlopes: number[];
  cutVolume: number;
  designElementId: number;
  edgeBreaklines: responseDataObject[][];
  equalCutSLopes: boolean;
  equalFillSlopes: boolean;
  fillSlopes: number[];
  fillVolume: number;
  length: number;
  name: string;
  orienAzimuth: number;
  rays: responseDataObject[][];
  type: string;
  vertices: responseDataObject[];
  width: number;
  wkid: string;
}

interface designElementTypeIdObject {
  id: number;
  name: string;
}

interface ISelectedPadsInfo {
  designElementId: number;
  designElementName: string;
  designElementTypeId: designElementTypeIdObject;
  sessionId: string;
  surveyName: string;
}

interface IPadSummaryTable {
  designElementId: number;
  designElementName: string;
  designElementTypeId: {
    id: number;
    name: string;
  };
  sessionId: string;
  surveyName: string;
  checked: boolean;
}

interface IPendingEdits {
  name: boolean; // Name does not require redesign before accept
  design: boolean; // numerical inputs do require redesign before accept
  unsavedEdits: boolean //UnsavedEdits is used for the user leaving warning pop up
}
